import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_WEIGHTS, SUB_SCORE_KEYS, type KpiWeights, type SubScoreKey } from '@rolefit/domain/fit';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Slider,
} from '@rolefit/ui';
import { queries } from '../lib/queries.js';
import { useAsync } from '../lib/useAsync.js';

// Fit weights are a Setting (not an interactive slider on the table). Saving
// them only reweights real sub-scores client-side; it never re-calls the model.
export function SettingsPage() {
  const profile = useAsync(() => queries.getProfile(), []);
  const [weights, setWeights] = useState<KpiWeights>(DEFAULT_WEIGHTS);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (profile.data?.kpi_weights && Object.keys(profile.data.kpi_weights).length) {
      setWeights({ ...DEFAULT_WEIGHTS, ...profile.data.kpi_weights });
    }
  }, [profile.data]);

  function setWeight(key: SubScoreKey, value: number) {
    setWeights((w) => ({ ...w, [key]: value }));
    setSaved(false);
  }

  async function save() {
    setBusy(true);
    setSaveError(null);
    try {
      const asNumbers = Object.fromEntries(Object.entries(weights).map(([k, v]) => [k, v ?? 0]));
      await queries.setWeights(asNumbers);
      setSaved(true);
    } catch {
      // Weights live on the profile; a user who hasn't onboarded has none yet.
      setSaveError('Import and confirm your profile first to save fit weights.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">Tune how much each dimension counts toward your fit KPI.</p>
      </header>

      <ProfileCard />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fit weights</CardTitle>
          <CardDescription>Each weight is 0 to 1. Ranking updates instantly; no model call.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {SUB_SCORE_KEYS.map((key) => (
            <div key={key} className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="capitalize">{key.replace('_', ' ')}</span>
                <span className="tabular-nums text-muted-foreground">{(weights[key] ?? 0).toFixed(2)}</span>
              </div>
              <Slider
                value={[(weights[key] ?? 0) * 100]}
                min={0}
                max={100}
                step={1}
                onValueChange={([v]) => setWeight(key, (v ?? 0) / 100)}
              />
            </div>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <Button onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save weights'}
            </Button>
            {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved</span>}
            {saveError && <span className="text-sm text-destructive">{saveError}</span>}
          </div>
        </CardContent>
      </Card>

      <MailboxCard />
      <ExtensionCard />
      <ResetCard />
    </div>
  );
}

// Generate/revoke personal API keys for the browser extension. Keys are scoped to
// ingest only (add roles + import profile text); the raw key is shown once.
function ExtensionCard() {
  const keys = useAsync(() => queries.listApiKeys(), []);
  const [name, setName] = useState('');
  const [created, setCreated] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const k = await queries.createApiKey(name.trim());
      setCreated(k.key);
      setName('');
      setCopied(false);
      keys.refetch();
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    await queries.revokeApiKey(id);
    setCreated(null);
    keys.refetch();
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-base">Browser extension</CardTitle>
        <CardDescription>
          Generate a key and paste it into the Rolefit browser extension to save roles and import your profile from any
          page you are viewing (LinkedIn, Greenhouse, Lever, and more). The key can only add roles and import profile
          text: it cannot send outreach or delete data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {created && (
          <div className="space-y-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3">
            <p className="text-sm font-medium">Your new key (copy it now, you will not see it again):</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 overflow-x-auto rounded bg-muted px-2 py-1 text-xs">{created}</code>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  await navigator.clipboard.writeText(created);
                  setCopied(true);
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="keyname" className="text-sm">
              New key name
            </Label>
            <Input
              id="keyname"
              placeholder="e.g. My laptop Chrome"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <Button onClick={create} disabled={busy || !name.trim()}>
            {busy ? 'Generating…' : 'Generate key'}
          </Button>
        </div>

        {keys.data && keys.data.length > 0 && (
          <ul className="divide-y rounded-md border text-sm">
            {keys.data.map((k) => (
              <li key={k.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <div>
                  <span className="font-medium">{k.name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {k.last_used_at ? `last used ${new Date(k.last_used_at).toLocaleDateString()}` : 'never used'}
                  </span>
                </div>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => revoke(k.id)}>
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// Entry point back into onboarding to re-import a CV (or a profile URL) or edit
// the parsed profile in place.
function ProfileCard() {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Profile</CardTitle>
        <CardDescription>
          Re-import your CV, import from a URL, or edit your experience, skills, and location. You confirm changes
          before they affect scoring.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild variant="outline">
          <Link to="/onboard">Re-import CV / edit profile</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

// Danger zone: wipe all of the user's data (two-step confirm), keeping the login.
function ResetCard() {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function reset() {
    setBusy(true);
    try {
      await queries.resetAccount();
      window.location.href = '/'; // reload into the empty state
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mt-6 border-destructive/40">
      <CardHeader>
        <CardTitle className="text-base text-destructive">Danger zone</CardTitle>
        <CardDescription>
          Delete all your data (roles, applications, packages, outreach, and mailbox connection) and start from scratch.
          Your login stays. This cannot be undone.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!confirming ? (
          <Button variant="outline" className="text-destructive" onClick={() => setConfirming(true)}>
            Reset — delete all my data
          </Button>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-sm">Delete everything and start over?</span>
            <Button variant="outline" className="text-destructive" disabled={busy} onClick={reset}>
              {busy ? 'Deleting…' : 'Yes, delete everything'}
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Connect Gmail for outreach. Least-privilege: drafts first; send is a separate,
// explicit grant. Disabled until the OAuth app is configured server-side.
function MailboxCard() {
  const conns = useAsync(() => queries.getConnections(), []);
  const gmail = conns.data?.gmail ?? null;
  const justConnected = new URLSearchParams(window.location.search).get('mailbox') === 'connected';

  async function connect(scope: 'draft' | 'send' | 'read') {
    const { url } = await queries.startGmailConnect(scope);
    window.location.href = url;
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-base">Mailbox</CardTitle>
        <CardDescription>
          Connect Gmail to draft and (opt-in) send outreach from your own mailbox. Least-privilege: drafts first, send
          is a separate grant. Nothing is ever sent without your explicit approval.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {justConnected && <p className="text-sm text-emerald-600 dark:text-emerald-400">Mailbox connected.</p>}
        {!conns.data?.mailbox_enabled ? (
          <p className="text-sm text-muted-foreground">Mailbox connect is not enabled yet.</p>
        ) : gmail ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="success">Gmail connected</Badge>
              <span className="text-muted-foreground">
                {[gmail.can_draft && 'drafts', gmail.can_send && 'send', gmail.can_read && 'read (tracking)']
                  .filter(Boolean)
                  .join(', ') || 'no scopes'}
              </span>
              {!gmail.can_read && (
                <Button size="sm" variant="outline" onClick={() => connect('read')}>
                  Connect inbox tracking (read)
                </Button>
              )}
              {!gmail.can_send && (
                <Button size="sm" variant="outline" onClick={() => connect('send')}>
                  Enable send
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={async () => {
                  await queries.disconnectMailbox();
                  conns.refetch();
                }}
              >
                Disconnect + purge
              </Button>
            </div>
            {gmail.can_read && (
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  onChange={(e) => queries.setMailboxAutoApply(e.target.checked)}
                  className="h-4 w-4"
                />
                Auto-apply high-confidence updates (skip the review queue)
              </label>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => connect('draft')}>
              Connect Gmail (drafts)
            </Button>
            <Button size="sm" variant="outline" onClick={() => connect('send')}>
              Connect with send
            </Button>
            <Button size="sm" variant="outline" onClick={() => connect('read')}>
              Connect for inbox tracking (read)
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
